import { ALERT_SEND_AGAIN_FROM, ALERT_SEND_ANYWAY_FROM } from '@nw/shared';
import type { AlertItem } from './api';

/**
 * The "send" button of an alert row in the alerts list. The status lists live in @nw/shared — the server
 * (AlertsPipelineService.sendAnyway) reads the same ones.
 *  - "שלח בכל זאת": skipped because the call was old or its rule is not selected (the person waives the skip)
 *  - "שלח שוב" (Orel 25.10): not sent because the client was not active yet, had no recipient yet, or the send failed —
 *    once the cause is fixed. The server checks it again (a draft client / no recipient still gets nothing, with the reason).
 * A watch alert (D-99) and a sent / queued alert get no button.
 */
export interface SendAction {
  label: string;
  title: string;
  confirm: string;
}

/** D-99: a watch alert is not about one call — the server refuses to send it anyway */
export const isWatchAlert = (a: Pick<AlertItem, 'ruleUuid'>): boolean => a.ruleUuid.startsWith('watch-');

const has = (list: readonly string[], status: string): boolean => list.includes(status);

export function sendActionOf(a: Pick<AlertItem, 'status' | 'ruleUuid'> & { sendRetrying?: boolean }): SendAction | null {
  if (isWatchAlert(a)) return null;
  // review 1: the Outbox still retries the failed message by itself — a re-send now would deliver it twice
  if (a.status === 'failed' && a.sendRetrying) return null;
  if (has(ALERT_SEND_AGAIN_FROM, a.status)) {
    return {
      label: 'שלח שוב',
      title: 'שליחת ההתראה שוב',
      confirm:
        a.status === 'failed'
          ? 'השליחה הקודמת נכשלה. לשלוח את ההתראה עכשיו לנמענים של הלקוח?'
          : 'לשלוח את ההתראה עכשיו לנמענים של הלקוח? אם הלקוח עדיין לא פעיל או שאין לו נמען להתראות, היא לא תישלח ונראה למה.',
    };
  }
  if (has(ALERT_SEND_ANYWAY_FROM, a.status)) {
    const why = a.status === 'skipped_rule' ? 'למרות שהכלל שלה לא נבחר להתראות' : 'למרות שהשיחה ישנה';
    return { label: 'שלח בכל זאת', title: 'שליחת ההתראה בכל זאת', confirm: `לשלוח את ההתראה הזאת לנמענים של הלקוח עכשיו, ${why}?` };
  }
  return null;
}
